import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import TemplateEditor, { previewString } from './TemplateEditor';
import Button from '../Button';

function getAxiosConfig() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
}

/** Relative time like "5d ago"; returns '' for null. */
function relativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function isSystem(t) {
  return t.is_system === true || t.user_id == null;
}

/**
 * TemplateLibrary — list of system + custom templates.
 *
 * Props:
 *  - onClose: () => void
 *  - onChange: () => void  optional, called when templates change (so the rail can refresh)
 */
export default function TemplateLibrary({ onClose, onChange }) {
  const { API_URL } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template object, or 'new', or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/coach-templates/`, getAxiosConfig());
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
    setLoading(false);
  }, [API_URL]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/coach-templates/${t.id}`, getAxiosConfig());
      await load();
      if (onChange) onChange();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete template.');
    }
  };

  const handleSaved = async () => {
    setEditing(null);
    await load();
    if (onChange) onChange();
  };

  if (editing) {
    return (
      <TemplateEditor
        template={editing === 'new' ? null : editing}
        onSaved={handleSaved}
        onCancel={() => setEditing(null)}
      />
    );
  }

  const systemTemplates = templates.filter(isSystem);
  const customTemplates = templates.filter((t) => !isSystem(t));

  const renderRow = (t, { deletable }) => (
    <div
      key={t.id}
      className="template-row"
      onClick={() => setEditing(t)}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 8px',
        borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>{t.name}</div>
        <div style={{
          display: 'block', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {previewString(t.parts) || '—'}
        </div>
      </div>
      {t.last_used_at && (
        <span style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {relativeTime(t.last_used_at)}
        </span>
      )}
      {deletable && (
        <Button
          type="button"
          variant="ghost"
          iconOnly
          aria-label={`Delete ${t.name}`}
          onClick={(e) => { e.stopPropagation(); handleDelete(t); }}
          style={{
            background: 'none', border: 'none', color: '#ef4444',
            fontSize: '16px', padding: '4px', lineHeight: 1,
          }}
        >
          🗑
        </Button>
      )}
    </div>
  );

  return (
    <div className="template-library">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div className="modal-title">Message templates</div>
        <Button variant="primary" onClick={() => setEditing('new')}>+ Create new</Button>
      </div>

      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
      ) : (
        <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
          {customTemplates.length > 0 && (
            <>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8', margin: '8px 0 4px' }}>
                Custom templates
              </div>
              {customTemplates.map((t) => renderRow(t, { deletable: true }))}
            </>
          )}

          {systemTemplates.length > 0 && (
            <>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8', margin: '14px 0 4px' }}>
                System templates
              </div>
              {systemTemplates.map((t) => renderRow(t, { deletable: false }))}
            </>
          )}

          {templates.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
              No templates yet. Create your first one.
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

export { relativeTime };
